import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Brain,
  CheckCircle2,
  ClipboardCheck,
  FileSearch,
  Gauge,
  Layers,
  ListChecks,
  Lock,
  Network,
  Route,
  Scale,
  ShieldCheck,
  Target,
  Users,
} from "lucide-react";

import SEO from "@/components/site/SEO";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";

const NTS_CATEGORIES = [
  {
    title: "Situational awareness",
    body:
      "Explores how candidates notice detail, maintain awareness, retain information, concentrate and anticipate risk.",
  },
  {
    title: "Diligence",
    body:
      "Assesses systematic working, checking behaviour and willingness to follow rules and procedures even under pressure.",
  },
  {
    title: "Communication",
    body:
      "Examines listening, clarity, assertiveness and appropriate sharing of operationally relevant information.",
  },
  {
    title: "Decision making and action",
    body:
      "Tests how candidates weigh information, make timely decisions and diagnose problems in realistic scenarios.",
  },
  {
    title: "Cooperation and working with others",
    body:
      "Considers respect, support, conflict management and willingness to challenge or assist colleagues appropriately.",
  },
  {
    title: "Workload management",
    body:
      "Looks at prioritisation, selective attention and calmness when tasks, distractions or pressures compete.",
  },
  {
    title: "Self-management",
    body:
      "Assesses motivation, confidence, initiative, preparedness, organisation and willingness to maintain knowledge.",
  },
];

const ASSESSMENT_AREAS = [
  {
    icon: Target,
    title: "Risk appetite",
    body:
      "Identifies whether a candidate tends to minimise, normalise, escalate or actively manage operational risk.",
  },
  {
    icon: ShieldCheck,
    title: "Rule adherence",
    body:
      "Tests whether rules are followed consistently, especially when there is pressure to avoid delay or keep services moving.",
  },
  {
    icon: Brain,
    title: "Non-technical skills",
    body:
      "Maps responses against railway NTS behaviours such as concentration, communication, decision making and self-management.",
  },
  {
    icon: Gauge,
    title: "Operational risk maturity",
    body:
      "Creates a structured profile of how mature, proportionate and defensible a candidate’s judgement appears to be.",
  },
  {
    icon: Route,
    title: "Task-based judgement",
    body:
      "Uses realistic railway tasks, degraded working, conflicting cues and hidden checks rather than abstract personality questions.",
  },
  {
    icon: FileSearch,
    title: "Evidence-led selection",
    body:
      "Supports recruitment decisions with structured behavioural evidence rather than relying only on interview impressions.",
  },
];

const BENEFITS = [
  {
    icon: Scale,
    title: "Standardised assessment",
    body:
      "Every candidate is assessed against the same scenario structure, scoring logic and risk maturity framework, reducing variation between individual managers, depots or interview panels.",
  },
  {
    icon: Users,
    title: "Reduced manager subjectivity",
    body:
      "RAT-BAT supports Driver Team Managers by giving them structured evidence, rather than relying solely on personal judgement, interview style or local interpretation of candidate responses.",
  },
  {
    icon: BarChart3,
    title: "Comparable candidate data",
    body:
      "Recruitment teams can compare applicants across a cohort using consistent scoring, NTS mapping and risk maturity indicators.",
  },
  {
    icon: ClipboardCheck,
    title: "Defensible selection evidence",
    body:
      "The assessment creates a clearer record of why a candidate may need further probing, development support or risk-based discussion at interview.",
  },
  {
    icon: ShieldCheck,
    title: "Earlier risk visibility",
    body:
      "Operators can identify behavioural risk themes before candidates enter expensive safety-critical training pipelines.",
  },
  {
    icon: Network,
    title: "Organisation-wide consistency",
    body:
      "RAT-BAT can help align recruitment standards across depots, regions and management teams, supporting a more consistent safety culture.",
  },
];

const DESIGN_PRINCIPLES = [
  "Every question gives the candidate enough information to answer without needing detailed rule book knowledge.",
  "Each scenario contains plausible options rather than one obviously correct answer and several obviously unsafe answers.",
  "Responses are scored against operational risk maturity, not simply pass or fail.",
  "The same scoring model is applied consistently across candidates, helping reduce variation caused by individual assessor interpretation.",
  "Some questions include hidden attention checks, such as reporting number mismatches, altered timings or conflicting operational details.",
  "Scenarios are designed around real railway pressures: delay, workload, uncertainty, communication, degraded working and social influence.",
  "The output is intended to support recruitment and development decisions, not replace existing selection, medical, psychometric or competence requirements.",
];

const OUTPUTS = [
  {
    title: "Candidate risk profile",
    body:
      "A structured summary showing how the candidate responded across risk, rules, pressure, uncertainty and NTS-aligned behaviours.",
  },
  {
    title: "NTS category scoring",
    body:
      "Scores can be grouped against the seven NTS categories to show stronger and weaker behavioural areas.",
  },
  {
    title: "Operational maturity ranking",
    body:
      "Operators can compare candidates using a consistent risk maturity framework rather than relying only on raw aptitude scores.",
  },
  {
    title: "Cohort comparison",
    body:
      "Recruitment teams can quickly compare applicants within a campaign, highlighting stronger candidates and identifying common risk themes across the group.",
  },
  {
    title: "Interview prompts",
    body:
      "Lower-scoring or unusual responses can be used to create targeted interview questions and follow-up probes.",
  },
  {
    title: "Development insight",
    body:
      "For successful candidates, the results can inform early development, onboarding, coaching and training focus areas.",
  },
  {
    title: "Manager support",
    body:
      "Driver Team Managers gain structured evidence to support judgement, reducing pressure to rely solely on subjective interview impressions.",
  },
  {
    title: "Assurance evidence",
    body:
      "The assessment can support a defensible, documented selection process when used alongside existing recruitment controls.",
  },
];

const USE_CASES = [
  {
    icon: Users,
    title: "Train driver recruitment",
    body:
      "Used before or alongside interview stages to explore how candidates think in safety-critical railway scenarios.",
  },
  {
    icon: ClipboardCheck,
    title: "Talent pool ranking",
    body:
      "Helps operators differentiate candidates who may have similar traditional aptitude or interview outcomes.",
  },
  {
    icon: Brain,
    title: "NTS-focused screening",
    body:
      "Provides an early indication of behaviours linked to concentration, communication, workload and decision making.",
  },
  {
    icon: AlertTriangle,
    title: "Risk culture insight",
    body:
      "Shows whether candidates tend to report, challenge and escalate, or whether they normalise risk and prioritise convenience.",
  },
  {
    icon: Layers,
    title: "Early training needs",
    body:
      "Can inform induction, rules training, simulator scenarios and early competence development.",
  },
  {
    icon: Network,
    title: "TRAC integration",
    body:
      "RAT-BAT can sit alongside TRAC-CMS and TRAC-Learn to connect recruitment insight with learning and competence evidence.",
  },
];

const RATBAT_FAQS = [
  {
    q: "What does RAT-BAT stand for?",
    a:
      "RAT-BAT stands for Risk and Task Based Aptitude Test. It is a scenario-based assessment concept designed to explore how candidates approach operational risk, rules, non-technical skills and safety-critical judgement.",
  },
  {
    q: "Is RAT-BAT a psychometric test?",
    a:
      "RAT-BAT is not intended to replace formal psychometric testing. It is designed as an additional railway-specific assessment tool that focuses on risk maturity, task judgement and non-technical skills in realistic operational scenarios.",
  },
  {
    q: "Why is standardisation important?",
    a:
      "In traditional recruitment processes, candidate assessment can vary depending on the individual Driver Team Manager, interviewer, depot or panel. RAT-BAT introduces a consistent scenario structure, scoring model and reporting framework so candidates can be assessed against the same evidence base.",
  },
  {
    q: "Would candidates need rule book knowledge?",
    a:
      "No. The assessment should provide enough information within each question for the candidate to make a judgement. The aim is to assess risk thinking, attention, decision making and rule attitude rather than detailed rule book recall.",
  },
  {
    q: "How are the answers scored?",
    a:
      "Each question can use five plausible responses scored from 1 to 5. A score of 5 represents stronger operational risk maturity, while a score of 1 indicates a weaker or more concerning response. The results can then be mapped against NTS categories and behavioural themes.",
  },
  {
    q: "Can RAT-BAT help compare candidates?",
    a:
      "Yes. RAT-BAT is designed to support cohort comparison, allowing operators to rank and compare applicants using consistent risk maturity scores, NTS category outputs and behavioural indicators.",
  },
  {
    q: "Can RAT-BAT be used for existing employees?",
    a:
      "Yes. Although the primary concept is recruitment support, RAT-BAT-style scenarios could also be used for development, NTS training, refresher learning, incident reflection and competence conversations.",
  },
  {
    q: "Does RAT-BAT make recruitment decisions automatically?",
    a:
      "No. RAT-BAT should support human decision making by providing structured evidence. It should be used alongside existing recruitment processes, psychometrics, interviews, medical standards and organisational selection controls.",
  },
];

export default function RATBAT() {
  return (
    <>
      <SEO
        title="RAT-BAT | Risk and Task Based Aptitude Test"
        description="RAT-BAT is a railway-focused risk and task-based aptitude testing concept designed to support safety-critical recruitment, NTS assessment, candidate comparison and operational risk maturity profiling."
        path="/ratbat"
      />

      <section
        className="relative overflow-hidden bg-navy py-20 md:py-28 text-white"
        data-testid="ratbat-hero"
      >
        <div className="container-rail">
          <div className="grid gap-10 lg:grid-cols-[1fr_380px] lg:items-center">
            <div>
              <p className="label-eyebrow text-white/70">
                Risk and Task Based Aptitude Test
              </p>

              <h1 className="mt-4 font-display font-bold text-4xl md:text-6xl leading-tight text-white">
                RAT-BAT — standardised safety-critical judgement assessment for
                rail recruitment.
              </h1>

              <p className="mt-6 max-w-3xl text-lg md:text-xl leading-relaxed text-white/80">
                A railway-specific assessment concept designed to help operators
                assess candidate risk maturity, rule attitude and non-technical
                skills using a consistent framework — reducing reliance on
                individual manager variation, local interpretation or subjective
                interview judgement alone.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-safety px-5 py-3 text-sm font-bold text-white hover:bg-safety/90 transition-colors"
                  data-testid="ratbat-hero-contact"
                >
                  Discuss RAT-BAT
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href="/ratbattest"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-navy px-5 py-3 text-sm font-bold hover:bg-slate-100 transition-colors"
                  data-testid="ratbat-hero-demo"
                >
                  Try Demo
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href="/for-organisations"
                  className="inline-flex items-center justify-center rounded-md border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10 transition-colors"
                  data-testid="ratbat-hero-trac"
                >
                  Back to TRAC
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 shadow-sm">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-md bg-white text-navy">
                <Scale className="h-8 w-8" />
              </div>

              <h2 className="mt-6 font-display text-2xl font-bold text-white leading-tight">
                A consistent framework for assessing safety-critical judgement.
              </h2>

              <p className="mt-4 text-white/80 leading-relaxed">
                RAT-BAT is intended to give operators a standardised behavioural
                risk lens. It supports Driver Team Managers by producing
                structured, comparable evidence rather than depending solely on
                individual interview style, experience, interpretation or local
                judgement.
              </p>

              <div className="mt-6 grid gap-3">
                {[
                  "Standardised candidate experience",
                  "Consistent scoring criteria",
                  "Reduced assessor variation",
                  "Comparable cohort reporting",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 rounded-md border border-white/10 bg-white/5 p-3"
                  >
                    <CheckCircle2 className="h-5 w-5 text-white" />
                    <p className="text-sm font-semibold text-white/90">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="ratbat-concept">
        <div className="container-rail">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <div>
              <p className="label-eyebrow text-safety">The concept</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                RAT-BAT is designed to sit between traditional aptitude testing,
                structured interview and operational competence assessment.
              </h2>

              <p className="mt-5 text-slate-600 leading-relaxed">
                Traditional recruitment processes are good at assessing core
                cognitive ability, attention, memory, reaction speed and
                structured interview evidence. However, they may not always show
                how a candidate thinks about realistic operational risk, or they
                may depend heavily on how individual managers interpret interview
                answers.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                RAT-BAT addresses that gap by presenting candidates with
                carefully designed railway scenarios. Each scenario gives enough
                information to make a decision, then asks the candidate to choose
                from a range of plausible responses. The aim is not to test
                detailed rule book knowledge. The aim is to assess judgement,
                risk awareness, diligence, concentration, escalation behaviour
                and willingness to follow procedure when competing pressures are
                present.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                Because every candidate is assessed against the same question
                logic, NTS mapping and scoring model, RAT-BAT can help operators
                create a more consistent, transparent and defensible approach to
                safety-critical recruitment.
              </p>
            </div>

            <div className="rounded-md bg-slate-50 border border-slate-200 p-6 md:p-8">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-navy text-white">
                <Target className="h-6 w-6" />
              </div>

              <h3 className="mt-5 font-display font-bold text-2xl text-navy leading-tight">
                The core question RAT-BAT asks
              </h3>

              <p className="mt-4 text-slate-600 leading-relaxed">
                When a candidate is faced with ambiguity, delay pressure,
                competing instructions, social pressure or a weak safety cue, do
                they:
              </p>

              <div className="mt-6 space-y-3">
                {[
                  "Notice the risk?",
                  "Challenge the assumption?",
                  "Follow the correct process?",
                  "Escalate at the right time?",
                  "Avoid normalising the unsafe condition?",
                  "Make a proportionate and defensible decision?",
                ].map((question) => (
                  <div
                    key={question}
                    className="flex items-start gap-3 rounded-md bg-white border border-slate-200 p-4"
                  >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-safety" />
                    <p className="text-sm font-semibold text-navy">
                      {question}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-slate-50"
        data-testid="ratbat-benefits"
      >
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Why operators use it</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              A more standardised way to assess candidate judgement and compare
              recruitment risk.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              One of RAT-BAT’s key benefits is consistency. It gives operators a
              structured assessment approach that is less dependent on individual
              Driver Team Manager variation, local interview habits or subjective
              interpretation of candidate answers.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((benefit, index) => {
              const Icon = benefit.icon;

              return (
                <div
                  key={benefit.title}
                  className="rounded-md border border-slate-200 bg-white p-6 shadow-sm hover:border-safety/60 transition-all"
                  data-testid={`ratbat-benefit-${index}`}
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h3 className="mt-4 font-display font-bold text-lg text-navy">
                    {benefit.title}
                  </h3>

                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {benefit.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-white"
        data-testid="ratbat-assessment-areas"
      >
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">What RAT-BAT assesses</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              A structured view of risk maturity, rules attitude and
              non-technical skills.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              RAT-BAT is designed to generate more useful recruitment evidence
              by looking at how candidates behave in realistic decision points,
              not simply how they describe themselves.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {ASSESSMENT_AREAS.map((area, index) => {
              const Icon = area.icon;

              return (
                <div
                  key={area.title}
                  className="rounded-md border border-slate-200 bg-slate-50 p-6 shadow-sm hover:border-safety/60 transition-all"
                  data-testid={`ratbat-assessment-area-${index}`}
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h3 className="mt-4 font-display font-bold text-lg text-navy">
                    {area.title}
                  </h3>

                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {area.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="ratbat-nts">
        <div className="container-rail">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="label-eyebrow text-safety">NTS-aligned design</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Built around railway non-technical skills.
              </h2>

              <p className="mt-5 text-slate-600 leading-relaxed">
                RAT-BAT can be mapped against the seven railway NTS categories.
                This allows results to be interpreted as a behavioural profile,
                showing where a candidate appears stronger or weaker in
                safety-critical judgement.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                The intended question bank structure is 104 questions: four
                questions for each of the 26 NTS behaviours. This gives operators
                a balanced assessment across situational awareness, diligence,
                communication, decision making, cooperation, workload management
                and self-management.
              </p>

              <div className="mt-8 rounded-md bg-navy p-6 text-white">
                <div className="flex items-start gap-3">
                  <ListChecks className="mt-1 h-6 w-6 shrink-0" />
                  <div>
                    <p className="font-display text-xl font-bold">
                      Proposed structure
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-white/80">
                      7 NTS categories · 26 NTS behaviours · 4 scenario
                      questions per behaviour · 104 questions in total.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {NTS_CATEGORIES.map((category, index) => (
                <div
                  key={category.title}
                  className="rounded-md border border-slate-200 bg-white p-5"
                  data-testid={`ratbat-nts-category-${index}`}
                >
                  <p className="text-xs font-bold uppercase tracking-wide text-safety">
                    Category {index + 1}
                  </p>

                  <h3 className="mt-2 font-display font-bold text-navy">
                    {category.title}
                  </h3>

                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {category.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-navy text-white"
        data-testid="ratbat-how-it-works"
      >
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-white/70">How it works</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl leading-tight">
              Scenario-based questions with five plausible responses and
              consistent scoring.
            </h2>

            <p className="mt-4 text-white/80 leading-relaxed">
              RAT-BAT questions are built to avoid the obvious “safe answer”
              problem. Each question should feel operationally realistic, with
              several possible responses that may appear reasonable at first
              glance. Each answer is then scored using the same risk maturity
              model, giving operators a more consistent basis for comparison.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-5">
            {[
              {
                number: "01",
                title: "Scenario",
                body:
                  "The candidate is given a realistic operational situation with relevant task, risk and context.",
              },
              {
                number: "02",
                title: "Pressure",
                body:
                  "The scenario includes competing pressures such as delay, workload, social influence or uncertainty.",
              },
              {
                number: "03",
                title: "Choice",
                body:
                  "The candidate chooses from five plausible responses rather than a simple right or wrong answer.",
              },
              {
                number: "04",
                title: "Scoring",
                body:
                  "Responses are scored from 1 to 5 against operational risk maturity and NTS alignment.",
              },
              {
                number: "05",
                title: "Profile",
                body:
                  "Scores are mapped into a candidate and cohort profile for recruitment, interview and development use.",
              },
            ].map((step) => (
              <div
                key={step.number}
                className="rounded-md border border-white/10 bg-white/5 p-5"
              >
                <p className="text-sm font-bold text-white/50">
                  {step.number}
                </p>

                <h3 className="mt-3 font-display text-lg font-bold">
                  {step.title}
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-white/75">
                  {step.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-md border border-white/10 bg-white/5 p-6 md:p-8">
            <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
              <div>
                <p className="label-eyebrow text-white/60">
                  Example scoring logic
                </p>

                <h3 className="mt-3 font-display text-2xl font-bold">
                  1 to 5 risk maturity scale.
                </h3>
              </div>

              <div className="grid gap-3 sm:grid-cols-5">
                {[
                  {
                    score: "1",
                    label: "High concern",
                  },
                  {
                    score: "2",
                    label: "Elevated risk",
                  },
                  {
                    score: "3",
                    label: "Mixed judgement",
                  },
                  {
                    score: "4",
                    label: "Generally sound",
                  },
                  {
                    score: "5",
                    label: "Strong maturity",
                  },
                ].map((item) => (
                  <div
                    key={item.score}
                    className="rounded-md bg-white p-4 text-center text-navy"
                  >
                    <p className="font-display text-3xl font-bold">
                      {item.score}
                    </p>
                    <p className="mt-1 text-xs font-semibold">
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-slate-50"
        data-testid="ratbat-design-principles"
      >
        <div className="container-rail">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="label-eyebrow text-safety">Assessment design</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Designed to reduce predictability, assessor variation and
                recruitment inconsistency.
              </h2>

              <p className="mt-5 text-slate-600 leading-relaxed">
                A common problem with situational judgement tests is that
                experienced candidates can often spot the “ideal” answer.
                Another challenge in recruitment is that different managers may
                probe, interpret or score candidate judgement differently.
              </p>

              <p className="mt-4 text-slate-600 leading-relaxed">
                RAT-BAT is designed to make the assessment more meaningful by
                using operationally plausible distractors, subtle detail changes,
                competing pressures and a standardised scoring model. This helps
                candidates show how they process risk while giving operators a
                more consistent evidence base.
              </p>
            </div>

            <div className="space-y-3">
              {DESIGN_PRINCIPLES.map((principle) => (
                <div
                  key={principle}
                  className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-4"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-safety" />
                  <p className="text-sm leading-relaxed text-slate-700">
                    {principle}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="ratbat-outputs">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Assessment outputs</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Useful evidence for selection, ranking, interview, assurance and
              early development.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              RAT-BAT is intended to produce information that a recruitment team,
              driver management team or training provider can actually use. The
              value is not just the final score, but the pattern of responses
              across safety-critical behaviours — and the ability to compare that
              pattern consistently across a candidate cohort.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {OUTPUTS.map((output, index) => (
              <div
                key={output.title}
                className="rounded-md border border-slate-200 bg-slate-50 p-6"
                data-testid={`ratbat-output-${index}`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                  <BarChart3 className="h-5 w-5" />
                </div>

                <h3 className="mt-4 font-display font-bold text-lg text-navy">
                  {output.title}
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {output.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-slate-50"
        data-testid="ratbat-use-cases"
      >
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Use cases</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Where RAT-BAT can support railway organisations.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              RAT-BAT is primarily designed as a recruitment enhancement tool,
              but the same scenario-based structure can support wider safety,
              learning and competence activity.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {USE_CASES.map((useCase, index) => {
              const Icon = useCase.icon;

              return (
                <div
                  key={useCase.title}
                  className="rounded-md border border-slate-200 bg-white p-6 shadow-sm hover:border-safety/60 transition-all"
                  data-testid={`ratbat-use-case-${index}`}
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h3 className="mt-4 font-display font-bold text-lg text-navy">
                    {useCase.title}
                  </h3>

                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {useCase.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="py-20 md:py-28 bg-navy text-white"
        data-testid="ratbat-governance"
      >
        <div className="container-rail">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <p className="label-eyebrow text-white/70">
                Governance and responsible use
              </p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl leading-tight">
                Built to support decisions, not automate them.
              </h2>

              <p className="mt-5 text-white/80 leading-relaxed">
                RAT-BAT should be used as part of a wider recruitment and
                assurance process. It should not replace existing selection
                standards, medical requirements, psychometric assessments,
                interviews or employer competence arrangements.
              </p>

              <p className="mt-4 text-white/80 leading-relaxed">
                The strongest application is as an additional evidence source:
                helping organisations ask better questions, compare candidates
                more consistently, identify risk themes earlier and make more
                structured decisions about suitability, support and development.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                "Clear scoring criteria",
                "Consistent candidate experience",
                "Reduced assessor variation",
                "Human review of results",
                "Evidence-led interview follow-up",
                "Bias and fairness monitoring",
                "Data protection and access control",
                "Audit trail of assessment decisions",
                "Regular review of question performance",
                "Cohort-level risk reporting",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-md border border-white/10 bg-white/5 p-4"
                >
                  <div className="flex items-start gap-3">
                    <Lock className="mt-0.5 h-5 w-5 shrink-0 text-white" />
                    <p className="text-sm text-white/85 leading-relaxed">
                      {item}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 rounded-md border border-white/10 bg-white/5 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <ShieldCheck className="mt-1 h-7 w-7 shrink-0 text-white" />
              <div>
                <p className="font-display text-xl font-bold">
                  ISO 27001-aligned thinking
                </p>

                <p className="mt-3 text-white/80 leading-relaxed">
                  As part of TRAC, RAT-BAT has been designed with appropriate
                  information security controls, role-based access, controlled
                  records, audit trails, retention rules and data governance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        items={RATBAT_FAQS}
        title="RAT-BAT FAQs"
        intro="Common questions about the Risk and Task Based Aptitude Test concept and how it can support standardised safety-critical railway recruitment."
        schemaId="faq-ratbat"
      />

      <CTASection
        eyebrow="RAT-BAT enquiry"
        title="Discuss RAT-BAT for your organisation."
        intro="Talk to us about using RAT-BAT to support standardised recruitment, candidate ranking, NTS assessment, risk maturity profiling or early-stage development for safety-critical railway roles."
        primary={{
          label: "Discuss RAT-BAT",
          to: "/contact",
          testId: "ratbat-cta-contact",
        }}
        secondary={{
          label: "Explore TRAC",
          to: "/for-organisations",
          testId: "ratbat-cta-trac",
        }}
      />
    </>
  );
}